/**
 * useClientErrorCapture - mount once in App.tsx. Captures every unhandled
 * browser error + promise rejection and persists it:
 *
 *   1. localStorage `pcb-client-errors` - ring buffer of last 50 errors so
 *      the history survives page reloads. Read from DevTools console:
 *        JSON.parse(localStorage['pcb-client-errors'])
 *   2. window.__PCB_ERRORS__ - same ring buffer in memory for quick access
 *      via the devtools console without parsing.
 *   3. console.error with a distinctive tag.
 *   4. client.retail.events.write() if the ApiClient is mounted (fire-and-forget,
 *      non-blocking, silently ignored on failure to avoid cascading crashes).
 *
 * This is the minimum viable blind-spot fix Bill asked for 2026-04-22:
 * "Any automatic logging we can do. It is crashing regularly now."
 *
 * To inspect from the Teams desktop client: right-click the tab -> Open
 * DevTools (if available) or add `?debug=1` to the URL, then in the console:
 *   window.__PCB_ERRORS__
 */
import { useEffect } from 'react';
import { useApiClient } from '@/contexts/ApiClientContext';

const STORAGE_KEY = 'pcb-client-errors';
const BUFFER_MAX = 50;

export interface CapturedError {
  when: string;                 // ISO timestamp
  kind: 'error' | 'unhandled-rejection' | 'react-boundary' | 'telemetry-failure' | 'telemetry-timeout';
  message: string;
  stack?: string;
  componentStack?: string;
  url?: string;
  userAgent?: string;
}

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    __PCB_ERRORS__?: CapturedError[];
  }
}

function loadBuffer(): CapturedError[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) return parsed.slice(-BUFFER_MAX) as CapturedError[];
  } catch { /* corrupted - start fresh */ }
  return [];
}

function persistBuffer(buf: CapturedError[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(buf.slice(-BUFFER_MAX)));
  } catch { /* quota exceeded or disabled - silent */ }
}

/**
 * Module-level appender so the React ErrorBoundary (class component) can call
 * the same capture path without going through a hook.
 */
export function appendCapturedError(e: CapturedError): void {
  if (typeof window === 'undefined') return;
  const buf = window.__PCB_ERRORS__ ?? loadBuffer();
  buf.push(e);
  while (buf.length > BUFFER_MAX) buf.shift();
  window.__PCB_ERRORS__ = buf;
  persistBuffer(buf);
  // eslint-disable-next-line no-console
  console.error('[PCB-Capture]', e.kind, e.message, e);
}

export function useClientErrorCapture(): void {
  const client = useApiClient();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Seed the in-memory buffer from whatever persisted from prior sessions.
    window.__PCB_ERRORS__ = loadBuffer();

    const forwardToServer = (e: CapturedError): void => {
      // Fire-and-forget. Swallow any transport failure so the error-logger
      // never causes its own cascading error.
      try {
        void client.retail.events.write({
          eventType: 'client.error.captured',
          eventCategory: 'system',
          severity: 'error',
          actor: 'client',
          timestamp: e.when as never,
          payloadJson: JSON.stringify(e),
        });
      } catch { /* silent */ }
    };

    const onError = (ev: ErrorEvent): void => {
      const captured: CapturedError = {
        when: new Date().toISOString(),
        kind: 'error',
        message: ev.message ?? 'unknown window.error',
        stack: ev.error?.stack ?? (ev.filename ? `${ev.filename}:${ev.lineno}:${ev.colno}` : undefined),
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      };
      appendCapturedError(captured);
      forwardToServer(captured);
    };

    const onRejection = (ev: PromiseRejectionEvent): void => {
      const reason: unknown = ev.reason;
      let message = 'unhandled rejection';
      let stack: string | undefined;
      if (reason instanceof Error) {
        message = reason.message;
        stack = reason.stack;
      } else if (typeof reason === 'string') {
        message = reason;
      } else {
        try { message = JSON.stringify(reason); } catch { /* default */ }
      }
      const captured: CapturedError = {
        when: new Date().toISOString(),
        kind: 'unhandled-rejection',
        message,
        stack,
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      };
      appendCapturedError(captured);
      forwardToServer(captured);
    };

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, [client]);
}
